import { Badge, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { Link } from '@/compat/react-router-dom';
import { useAboutPage, getAboutSectionByKey } from '@/hooks/useAboutPage';
import { getAboutImage } from '@/utils/imageUrl';

const Hero = ({ sectionData: propSectionData }) => {
  const { sections, loading } = useAboutPage();
  const heroSection = propSectionData || getAboutSectionByKey(sections, 'hero');
  
  const bgColor = heroSection?.background_color || 'light';
  const bgEffect = heroSection?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  // Apply gradient effect if selected
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';
  
  if (loading && !propSectionData) {
    return (
      <section className={`about-plan py-4 py-lg-5 ${bgClass} about-section about-plan-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
        {hasDotsEffect && (
          <>
            <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
            <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
          </>
        )}
        <Container className={hasDotsEffect ? 'position-relative' : ''}>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }
  
  if (!heroSection) {
    return null;
  }
  
  const galleryImages = heroSection.content?.gallery_images || [];
  const featureItems = heroSection.items || [];
  const ctaText = heroSection.content?.cta_text || 'Contact our team';
  const ctaLink = heroSection.content?.cta_link || '/contact';
  
  const buildPlaceholder = (width, height, label) => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <defs>
          <linearGradient id="bg" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#f2f2f2"/>
            <stop offset="100%" stop-color="#e5e5e5"/>
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" rx="24" ry="24" fill="url(#bg)"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Montserrat, Arial, sans-serif" font-size="32" fill="#111111">${label}</text>
      </svg>
    `;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  };

  const withFallback = (imagePath, width, height, label) => {
    if (!imagePath) return buildPlaceholder(width, height, label);
    const fullUrl = getAboutImage(imagePath);
    return fullUrl || buildPlaceholder(width, height, label);
  };

  const photos = [
    {
      src: withFallback(galleryImages[0], 424, 498, '424 × 498'),
      alt: 'Curated excursions across Eastern Crete',
      className: 'about-plan__photo about-plan__photo--main shadow-lg',
      width: 424,
      height: 498
    },
    {
      src: withFallback(galleryImages[1], 434, 508, '434 × 508'),
      alt: 'Local guides welcoming travellers',
      className: 'about-plan__photo about-plan__photo--overlay shadow',
      width: 434,
      height: 508
    },
    {
      src: withFallback(galleryImages[2], 424, 320, '424 × 320'),
      alt: 'Guests exploring the coast with our team',
      className: 'about-plan__photo about-plan__photo--floating shadow-lg movingX',
      width: 424,
      height: 320
    }
  ].filter(photo => photo.src); // Only include photos that have a source

  return (
    <section className={`about-plan py-4 py-lg-5 ${bgClass} about-section about-plan-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container className={hasDotsEffect ? 'position-relative' : ''}>
        <Row className="g-4 align-items-start about-plan__row">
          {photos.length > 0 && (
            <Col lg={7} className="order-lg-1 about-plan__column ps-0">
              <div className="about-plan__gallery">
                {photos.map(({ src, alt, className, width, height }, idx) => (
                  <img key={idx} src={src} alt={alt} width={width} height={height} className={className} loading="lazy" />
                ))}
              </div>
            </Col>
          )}

          <Col lg={photos.length > 0 ? 5 : 12} className="order-lg-2 about-plan__content-column ps-lg-4">
            <div className="about-plan__content">
              {heroSection.subtitle && (
                <Badge bg="primary" className="bg-opacity-10 text-primary mb-3">
                  {heroSection.subtitle}
                </Badge>
              )}
              {heroSection.title && (
                <h2 className="sec-title mb-3">{heroSection.title}</h2>
              )}
              {heroSection.intro_text && (
                <p className="lead intro-text mb-4" dangerouslySetInnerHTML={{ __html: heroSection.intro_text }} />
              )}

              {featureItems.length > 0 && (
                <div className="about-plan__features mb-4">
                  {featureItems.map((item) => {
                    const Icon = item.IconComponent;
                    const normalizedColor = (item.icon_color || 'primary').toString().trim().toLowerCase();
                    const colorClass =
                      normalizedColor === 'primary' ? 'primary' :
                      normalizedColor === 'success' ? 'success' :
                      normalizedColor === 'info' ? 'info' :
                      normalizedColor === 'warning' ? 'warning' :
                      normalizedColor === 'danger' ? 'danger' :
                      normalizedColor === 'secondary' ? 'secondary' :
                      normalizedColor === 'dark' ? 'dark' :
                      'primary';
                    return (
                      <div key={item.id} className="about-plan__feature">
                        {Icon && (
                          <span className={`about-plan__feature-icon bg-${colorClass} bg-opacity-10 text-${colorClass}`}>
                            <Icon />
                          </span>
                        )}
                        <div>
                          <h6 className="about-plan__feature-title mb-1 fw-bold" style={{ color: '#000000' }}>{item.title}</h6>
                          {item.description && (
                            <p className="about-plan__feature-description mb-0" dangerouslySetInnerHTML={{ __html: item.description }} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="text-center">
                <Button as={Link} to={ctaLink} variant="primary" size="lg" className="px-5">
                  {ctaText}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Hero;
